import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectPath?: string;
  loadingComponent?: React.ReactNode;
  requireAdmin?: boolean;
}

/**
 * A component that protects routes from unauthorized access.
 * Redirects to login if user is not authenticated.
 * Optionally can require admin privileges.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectPath = '/login',
  loadingComponent = (
    <div className="min-h-screen flex items-center justify-center bg-gray-950">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto mb-4"></div>
        <p className="text-gray-400">Loading...</p>
      </div>
    </div>
  ),
  requireAdmin = false,
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const hasToken = Boolean(localStorage.getItem('token'));

  // Show loading state while checking authentication
  if (loading) {
    return <>{loadingComponent}</>;
  }

  // If token exists but user profile couldn't be fetched (e.g. transient backend 5xx),
  // keep user on a reconnect state instead of forcing logout-style redirect.
  if (!user) {
    if (hasToken) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-950">
          <div className="text-center px-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto mb-4"></div>
            <p className="text-slate-200 font-medium">Reconnecting to server...</p>
            <p className="text-slate-400 text-sm mt-1">Please wait a moment and try again.</p>
          </div>
        </div>
      );
    }
    // Save the current location to redirect back after login
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // Check for admin privileges if required
  if (requireAdmin && !user.is_admin) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated (and admin if required), render the children
  return <>{children}</>;
};

export default ProtectedRoute;
